import { useState } from 'react'
import { AreaTexto, Campo, Entrada } from '@/components/ui/Campo'
import { SelectorFecha } from '@/components/ui/SelectorFecha'
import { Boton } from '@/components/ui/Boton'
import {
  Dialogo,
  DialogoCerrar,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoEncabezado,
  DialogoPie,
  DialogoTitulo,
} from '@/components/ui/Dialogo'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { mensajeDeError } from '@/features/admin/ModalMarco'
import { cn } from '@/lib/cn'
import {
  ETIQUETA_RED,
  ETIQUETA_TIPO,
  LAMINAS,
  REDES,
  TIPOS,
  TOPE_COPY,
  TOPE_TITULO,
  enlaceDrive,
  leerArchivosPegados,
  publicacionEntrada,
  type DestinoPublicacion,
  type PublicacionDetalle,
  type PublicacionEntrada,
  type RedSocial,
  type TipoPublicacion,
} from '@/lib/schemas/publicaciones'
import { hoyEnBuenosAires } from '@/features/grilla/formato'
import type { ArchivoDeDrive } from '@/lib/drive/useElegirDeDrive'
import { useDrivePublico, useEnviarARevision, useGuardarPublicacion } from './api'
import { BotonDrive, ListaArchivos } from './ArchivosDeDrive'
import { CampoHora, SelectorDestino } from './CamposMomento'
import { VistaPrevia } from './VistaPrevia'

interface ModalPublicacionProps {
  abierto: boolean
  alCerrar: () => void
  proyectoId: string
  /** El @ del cliente, para la vista previa con aspecto de Instagram. */
  usuarioInstagram: string
  pilares: { id: string; nombre: string; color: number }[]
  /** Sin publicación: alta. Con una en borrador: edición completa. */
  publicacion: PublicacionDetalle | null
  /** Alta desde el "+" de un día del calendario: la fecha viene puesta (§8.3). */
  fechaInicial?: string | null
  /** Carpeta de Drive del cliente: ahí se abre el selector (Fase 6). */
  carpetaDrive?: string | null
}

/**
 * C5 · modal de publicación, modo nueva y edición de borrador (§8.5).
 *
 * Archivos (Fase 6): "Elegir de Drive" abre el selector en la carpeta del
 * cliente y valida cada archivo con lo que informa Drive (formato, peso,
 * duración y proporción). Pegar el enlace sigue andando, como respaldo. El
 * campo de texto es la fuente de verdad del orden: el selector y el arrastre
 * de las láminas lo reescriben. La vista previa en vivo ocupa la columna
 * derecha en escritorio y se despliega bajo demanda en celular.
 */
export function ModalPublicacion(props: ModalPublicacionProps) {
  if (!props.abierto) return null
  // La fecha entra en la clave: abrir el "+" de otro día rearma el formulario.
  return (
    <Formulario
      key={props.publicacion?.id ?? `nueva-${props.fechaInicial ?? ''}`}
      {...props}
    />
  )
}

interface Valores {
  titulo: string
  tipo: TipoPublicacion
  destino: DestinoPublicacion
  archivosTexto: string
  portadaTexto: string
  copy: string
  fechaPautada: string
  /** "HH:MM" o vacía: sin hora (DEC-20). */
  horaPautada: string
  pilarIds: string[]
  redes: RedSocial[]
}

function Formulario({
  abierto,
  alCerrar,
  proyectoId,
  usuarioInstagram,
  pilares,
  publicacion,
  fechaInicial,
  carpetaDrive = null,
}: ModalPublicacionProps) {
  const guardar = useGuardarPublicacion()
  const enviar = useEnviarARevision()
  const avisar = useAvisar()
  const [v, setV] = useState<Valores>(() => inicial(publicacion, fechaInicial))
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [verPrevia, setVerPrevia] = useState(false)
  /** Lo que se eligió con el selector: nombre, metadata y validación, por id. */
  const [deDrive, setDeDrive] = useState<Record<string, ArchivoDeDrive>>({})

  const archivos = leerArchivosPegados(v.archivosTexto)
  const portada = leerArchivosPegados(v.portadaTexto)
  const idPortada = v.tipo === 'video' ? portada.ids[0] : undefined
  // Lo elegido con el selector hereda los permisos de la carpeta del cliente:
  // sólo los enlaces pegados a mano se revisan contra "cualquiera con el enlace".
  const usados = idPortada ? [...archivos.ids, idPortada] : archivos.ids
  const pegados = usados.filter((id) => !(id in deDrive))
  const publicos = useDrivePublico(pegados)
  // Nombres de los archivos que Drive no muestra sin sesión, para el aviso.
  const privados = [
    ...archivos.ids
      .map((id, i) =>
        publicos.data?.[id] === false
          ? v.tipo === 'carrusel'
            ? `lámina ${String(i + 1)}`
            : 'el archivo'
          : null,
      )
      .filter((x): x is string => x !== null),
    ...(idPortada && publicos.data?.[idPortada] === false ? ['la portada'] : []),
  ]
  const privadosIds = new Set(pegados.filter((id) => publicos.data?.[id] === false))
  const rechazados = usados.filter((id) => deDrive[id]?.validacion.estado === 'rechazo')
  const validacionPortada = idPortada ? deDrive[idPortada]?.validacion : undefined

  const recordar = (lista: ArchivoDeDrive[]) => {
    setDeDrive((a) => ({ ...a, ...Object.fromEntries(lista.map((x) => [x.id, x])) }))
  }
  const comoTexto = (ids: string[]) => ids.map(enlaceDrive).join('\n')

  const cambiar = <K extends keyof Valores>(k: K, valor: Valores[K]) => {
    setV((a) => ({ ...a, [k]: valor }))
    setErrores({})
    setErrorGeneral(null)
  }

  const armar = (): PublicacionEntrada => ({
    proyectoId,
    titulo: v.titulo,
    tipo: v.tipo,
    destino: v.destino,
    archivos: archivos.ids,
    portada: portada.ids[0] ?? '',
    copy: v.copy,
    fechaPautada: v.fechaPautada,
    horaPautada: v.horaPautada,
    pilarIds: v.pilarIds,
    redes: v.redes,
    metadatos: Object.fromEntries(
      usados.flatMap((id) => {
        const d = deDrive[id]
        return d ? [[id, d.metadato]] : []
      }),
    ),
  })

  const validar = (): PublicacionEntrada | null => {
    if (archivos.invalidos.length > 0) {
      setErrores({ archivos: 'Pegá enlaces de Drive o sus identificadores.' })
      return null
    }
    if (rechazados.length > 0) {
      setErrores({
        archivos:
          rechazados.length === 1
            ? 'Hay un archivo que no se puede publicar: cambialo o quitalo.'
            : 'Hay archivos que no se pueden publicar: cambialos o quitalos.',
      })
      return null
    }
    const datos = armar()
    const r = publicacionEntrada.safeParse(datos)
    if (!r.success) {
      const porCampo: Record<string, string> = {}
      for (const issue of r.error.issues) {
        const clave = String(issue.path[0] ?? '')
        porCampo[clave] ??= issue.message
      }
      setErrores(porCampo)
      return null
    }
    return datos
  }

  const enviarFormulario = (tambienARevision: boolean) => {
    const datos = validar()
    if (!datos) return
    setOcupado(true)
    setErrorGeneral(null)
    guardar
      .mutateAsync({ id: publicacion?.id ?? null, datos })
      .then(async (p) => {
        if (tambienARevision) {
          await enviar.mutateAsync(p.id)
          avisar(`${p.titulo} enviada a revisión`)
        } else {
          avisar(publicacion ? 'Borrador guardado' : `${p.titulo} guardada como borrador`)
        }
        alCerrar()
      })
      .catch((e: unknown) => {
        setErrorGeneral(mensajeDeError(e))
      })
      .finally(() => {
        setOcupado(false)
      })
  }

  const pilaresElegidos = pilares
    .filter((p) => v.pilarIds.includes(p.id))
    .map((p) => p.nombre)

  const previa = (
    <VistaPrevia
      usuarioInstagram={usuarioInstagram}
      tipo={v.tipo}
      copy={v.copy}
      archivos={archivos.ids}
      portada={idPortada}
      pilares={pilaresElegidos}
      redes={v.redes}
    />
  )

  return (
    <Dialogo
      open={abierto}
      onOpenChange={(o) => {
        if (!o) alCerrar()
      }}
    >
      <DialogoContenido tamano="grande" aria-busy={ocupado}>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault()
          }}
          noValidate
        >
          <DialogoEncabezado>
            <DialogoTitulo className="text-xl font-semibold tracking-tight">
              {publicacion ? 'Editar publicación' : 'Nueva publicación'}
            </DialogoTitulo>
            <DialogoDescripcion className="sr-only">
              Datos de la publicación y vista previa
            </DialogoDescripcion>
          </DialogoEncabezado>

          <DialogoCuerpo className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] lg:gap-8">
            <div className="flex flex-col gap-4">
              <Campo
                etiqueta="Título"
                error={errores.titulo}
                extremo={`${String(v.titulo.length)} / ${String(TOPE_TITULO)}`}
              >
                {(p) => (
                  <Entrada
                    id={p.id}
                    aria-invalid={p.invalido}
                    value={v.titulo}
                    onChange={(e) => {
                      cambiar('titulo', e.target.value)
                    }}
                    autoFocus
                    maxLength={TOPE_TITULO}
                    placeholder="Bienvenida al consultorio"
                  />
                )}
              </Campo>

              <fieldset>
                <legend className="mb-2 text-sm font-medium">Tipo</legend>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Tipo">
                  {TIPOS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={v.tipo === t}
                      disabled={publicacion !== null && publicacion.estado !== 'borrador'}
                      onClick={() => {
                        cambiar('tipo', t)
                      }}
                      className={cn(
                        'min-h-11 rounded-full px-5 text-sm font-medium',
                        v.tipo === t
                          ? 'bg-tinta text-white'
                          : 'border border-borde bg-tarjeta text-tinta hover:bg-lienzo',
                      )}
                    >
                      {ETIQUETA_TIPO[t]}
                    </button>
                  ))}
                </div>
              </fieldset>

              <SelectorDestino
                valor={v.destino}
                alCambiar={(d) => {
                  cambiar('destino', d)
                }}
              />

              <BotonDrive
                carpetaId={carpetaDrive}
                multiple={v.tipo === 'carrusel'}
                espera={v.tipo === 'video' ? 'video' : 'imagen'}
                titulo={v.tipo === 'carrusel' ? 'Elegí las láminas' : 'Elegí el archivo'}
                etiqueta={
                  v.tipo === 'carrusel' ? 'Elegir láminas de Drive' : 'Elegir de Drive'
                }
                alElegir={(lista) => {
                  recordar(lista)
                  const nuevos = lista.map((x) => x.id)
                  cambiar(
                    'archivosTexto',
                    comoTexto(
                      v.tipo === 'carrusel'
                        ? [
                            ...archivos.ids,
                            ...nuevos.filter((id) => !archivos.ids.includes(id)),
                          ]
                        : nuevos.slice(0, 1),
                    ),
                  )
                }}
              />

              <Campo
                etiqueta="Archivos"
                ayuda={
                  v.tipo === 'carrusel'
                    ? `De ${String(LAMINAS.min)} a ${String(LAMINAS.max)} láminas. Elegilas de Drive, o pegá un enlace por línea, en orden.`
                    : 'Elegilo de Drive, o pegá el enlace del archivo.'
                }
                error={errores.archivos}
                extremo={
                  archivos.ids.length > 0
                    ? `${String(archivos.ids.length)} ${archivos.ids.length === 1 ? 'archivo' : 'archivos'}`
                    : undefined
                }
              >
                {(p) => (
                  <AreaTexto
                    id={p.id}
                    aria-invalid={p.invalido}
                    aria-describedby={p.describedBy}
                    value={v.archivosTexto}
                    onChange={(e) => {
                      cambiar('archivosTexto', e.target.value)
                    }}
                    rows={v.tipo === 'carrusel' ? 4 : 2}
                    placeholder="https://drive.google.com/file/d/…/view"
                  />
                )}
              </Campo>

              <ListaArchivos
                ids={archivos.ids}
                info={Object.fromEntries(
                  archivos.ids.map((id) => {
                    const d = deDrive[id]
                    return [
                      id,
                      d ? { nombre: d.nombre, validacion: d.validacion } : undefined,
                    ]
                  }),
                )}
                carrusel={v.tipo === 'carrusel'}
                privados={privadosIds}
                alReordenar={(ids) => {
                  cambiar('archivosTexto', comoTexto(ids))
                }}
                alQuitar={(id) => {
                  cambiar(
                    'archivosTexto',
                    comoTexto(archivos.ids.filter((x) => x !== id)),
                  )
                }}
              />

              {privados.length > 0 && (
                <p
                  role="status"
                  data-aviso-drive
                  className="rounded-2xl bg-cambios px-4 py-3 text-sm text-cambios-tinta"
                >
                  {privados.length === 1 ? 'Hay un archivo' : 'Hay archivos'} (
                  {privados.join(', ')}) que el cliente no va a poder ver: en Drive no{' '}
                  {privados.length === 1 ? 'está compartido' : 'están compartidos'} con
                  «Cualquier persona con el enlace». Abrilo, tocá Compartir y cambiá el
                  acceso general.
                </p>
              )}

              {v.tipo === 'video' && (
                <BotonDrive
                  carpetaId={carpetaDrive}
                  multiple={false}
                  espera="imagen"
                  titulo="Elegí la portada"
                  etiqueta="Elegir portada de Drive"
                  alElegir={(lista) => {
                    recordar(lista)
                    const id = lista[0]?.id
                    if (id) cambiar('portadaTexto', enlaceDrive(id))
                  }}
                />
              )}

              {v.tipo === 'video' &&
                validacionPortada &&
                validacionPortada.estado !== 'ok' && (
                  <p
                    role={validacionPortada.estado === 'rechazo' ? 'alert' : 'status'}
                    data-validacion={validacionPortada.estado}
                    className="rounded-xl bg-cambios px-3.5 py-2.5 text-sm text-cambios-tinta"
                  >
                    <strong>La portada:</strong> {validacionPortada.mensaje}
                  </p>
                )}

              {v.tipo === 'video' && (
                <Campo
                  etiqueta="Portada"
                  ayuda="Imagen que se ve antes de reproducir. También sale de Drive."
                  error={errores.portada}
                >
                  {(p) => (
                    <Entrada
                      id={p.id}
                      aria-invalid={p.invalido}
                      aria-describedby={p.describedBy}
                      value={v.portadaTexto}
                      onChange={(e) => {
                        cambiar('portadaTexto', e.target.value)
                      }}
                      placeholder="https://drive.google.com/file/d/…/view"
                    />
                  )}
                </Campo>
              )}

              <Campo
                etiqueta="Copy"
                error={errores.copy}
                extremo={`${String(v.copy.length)} / ${String(TOPE_COPY)}`}
              >
                {(p) => (
                  <AreaTexto
                    id={p.id}
                    aria-invalid={p.invalido}
                    value={v.copy}
                    onChange={(e) => {
                      cambiar('copy', e.target.value)
                    }}
                    rows={5}
                    maxLength={TOPE_COPY}
                  />
                )}
              </Campo>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <Campo
                  etiqueta="Fecha pautada"
                  error={errores.fechaPautada}
                  className="sm:flex-1"
                >
                  {(p) => (
                    <SelectorFecha
                      id={p.id}
                      invalido={p.invalido}
                      valor={v.fechaPautada}
                      alCambiar={(fecha) => {
                        cambiar('fechaPautada', fecha)
                      }}
                    />
                  )}
                </Campo>
                <CampoHora
                  valor={v.horaPautada}
                  error={errores.horaPautada}
                  alCambiar={(hora) => {
                    cambiar('horaPautada', hora)
                  }}
                />
              </div>

              <fieldset>
                <legend className="mb-2 text-sm font-medium">Pilares</legend>
                {pilares.length === 0 ? (
                  <p className="text-sm text-tinta-suave">
                    Este proyecto no tiene pilares todavía. Se crean desde Administración.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {pilares.map((p) => {
                      const elegido = v.pilarIds.includes(p.id)
                      return (
                        <button
                          key={p.id}
                          type="button"
                          aria-pressed={elegido}
                          onClick={() => {
                            cambiar(
                              'pilarIds',
                              elegido
                                ? v.pilarIds.filter((x) => x !== p.id)
                                : [...v.pilarIds, p.id],
                            )
                          }}
                          className={cn(
                            'inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm',
                            elegido
                              ? 'bg-tinta text-white'
                              : 'border border-borde bg-tarjeta hover:bg-lienzo',
                          )}
                        >
                          <span
                            className="size-2.5 rounded-full"
                            style={{
                              backgroundColor: `var(--color-pilar-${String(p.color)})`,
                            }}
                            aria-hidden
                          />
                          {p.nombre}
                        </button>
                      )
                    })}
                  </div>
                )}
                {errores.pilarIds && (
                  <p role="alert" className="mt-1.5 text-xs text-nuevo">
                    {errores.pilarIds}
                  </p>
                )}
              </fieldset>

              <fieldset>
                <legend className="mb-2 text-sm font-medium">Redes</legend>
                <div className="flex flex-wrap gap-2">
                  {REDES.map((r) => {
                    const elegida = v.redes.includes(r)
                    return (
                      <button
                        key={r}
                        type="button"
                        aria-pressed={elegida}
                        onClick={() => {
                          cambiar(
                            'redes',
                            elegida ? v.redes.filter((x) => x !== r) : [...v.redes, r],
                          )
                        }}
                        className={cn(
                          'min-h-11 rounded-full px-4 text-sm',
                          elegida
                            ? 'bg-tinta text-white'
                            : 'border border-borde bg-tarjeta hover:bg-lienzo',
                        )}
                      >
                        {ETIQUETA_RED[r]}
                      </button>
                    )
                  })}
                </div>
                <p className="mt-1.5 text-xs text-tinta-suave">
                  La vista previa es siempre de Instagram.
                </p>
                {errores.redes && (
                  <p role="alert" className="mt-1.5 text-xs text-nuevo">
                    {errores.redes}
                  </p>
                )}
              </fieldset>

              <Boton
                variante="secundario"
                className="lg:hidden"
                onClick={() => {
                  setVerPrevia((x) => !x)
                }}
              >
                {verPrevia ? 'Ocultar vista previa' : 'Ver vista previa'}
              </Boton>
              {verPrevia && <div className="lg:hidden">{previa}</div>}

              {errorGeneral && (
                <p
                  role="alert"
                  className="rounded-xl bg-nuevo/10 px-3.5 py-2.5 text-sm text-nuevo"
                >
                  {errorGeneral}
                </p>
              )}
            </div>

            <div className="hidden lg:block">{previa}</div>
          </DialogoCuerpo>

          <DialogoPie>
            <DialogoCerrar asChild>
              <Boton variante="secundario" disabled={ocupado}>
                Cancelar
              </Boton>
            </DialogoCerrar>
            <Boton
              variante="secundario"
              disabled={ocupado}
              onClick={() => {
                enviarFormulario(false)
              }}
            >
              Guardar borrador
            </Boton>
            <Boton
              disabled={ocupado || archivos.ids.length === 0 || rechazados.length > 0}
              onClick={() => {
                enviarFormulario(true)
              }}
            >
              Enviar a revisión
            </Boton>
          </DialogoPie>
        </form>
      </DialogoContenido>
    </Dialogo>
  )
}

function inicial(p: PublicacionDetalle | null, fechaInicial?: string | null): Valores {
  if (!p) {
    return {
      titulo: '',
      tipo: 'imagen',
      destino: 'feed',
      archivosTexto: '',
      portadaTexto: '',
      copy: '',
      fechaPautada: fechaInicial ?? hoyEnBuenosAires(),
      horaPautada: '',
      pilarIds: [],
      redes: ['instagram'],
    }
  }
  return {
    titulo: p.titulo,
    tipo: p.tipo,
    destino: p.destino,
    archivosTexto: p.archivos
      .filter((a) => a.rol !== 'portada')
      .map((a) => enlaceDrive(a.driveFileId))
      .join('\n'),
    portadaTexto: p.archivos.find((a) => a.rol === 'portada')?.driveFileId
      ? enlaceDrive(p.archivos.find((a) => a.rol === 'portada')?.driveFileId ?? '')
      : '',
    copy: p.copy,
    fechaPautada: p.fechaPautada,
    horaPautada: p.horaPautada ?? '',
    pilarIds: p.pilares.map((x) => x.id),
    redes: p.redes,
  }
}
